import React, {
  startTransition,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  InfographicViewer,
  EditorialImage,
  ComparisonDecisionSelector,
  DimensionScorecard,
  ListiclePodium,
  TrustSignalsGrid,
} from "./VisualSemanticsComponents";

const API_BASE = process.env.REACT_APP_API_BASE || "/api/public";
const DEFAULT_DESCRIPTION =
  "Guía editorial de Cursos a Distancia con reseñas, comparativas y listicles conectados a los programas oficiales del directorio.";

function getComparisonInfographic(item) {
  const slug = (item.slug || "").toLowerCase();
  const area = (item.area || "").toLowerCase();
  const subject = item.title || "esta comparativa";

  if (slug.includes("utn") || slug.includes("siglo-21") || slug.includes("diplomatura")) {
    return {
      src: "/images/infografias/infografia-diplomatura-vs-certificacion-universitaria.svg",
      title: "Jerarquía de Validez: Diplomatura Universitaria vs Certificación Continua",
      alt: `Criterios visuales para interpretar ${subject}: respaldo, alcance y carga horaria declarada.`,
      caption:
        "La modalidad y el respaldo no son equivalentes por sí solos: revisa quién emite la constancia, qué resolución declara y qué alcance tiene antes de comparar.",
    };
  }

  if (
    slug.includes("programacion") ||
    slug.includes("full-stack") ||
    area.includes("desarrollo") ||
    area.includes("programaci")
  ) {
    return {
      src: "/images/infografias/infografia-bootcamp-vs-carrera-modular.svg",
      title: "Criterios para comparar rutas de desarrollo web",
      alt: `Comparación visual de los criterios de ${subject}: ritmo, acompañamiento, profundidad y proyecto.`,
      caption:
        "Separa ritmo de cursada, acompañamiento, profundidad técnica, proyectos y requisitos de cada programa; la ficha oficial determina qué características aplican.",
    };
  }

  return {
    src: "/images/infografias/infografia-ecosistema-cursos-latam.svg",
    title: "Ecosistema Educativo Online: Modelos Formativos en Comparación",
    alt: `Mapa visual del contexto de ${subject}: instituciones, programas y modalidades de formación online.`,
    caption:
      "Compara flexibilidad, acompañamiento, nivel y certificación según los datos declarados para cada institución o programa.",
  };
}

function getListicleInfographic(item) {
  const slug = (item.slug || "").toLowerCase();
  const area = (item.area || "").toLowerCase();

  if (
    slug.includes("full-stack") ||
    slug.includes("programacion") ||
    slug.includes("desarrollo") ||
    area.includes("desarrollo") ||
    area.includes("programaci")
  ) {
    return {
      src: "/images/infografias/infografia-ruta-aprendizaje-desarrollo-web.svg",
      title: "Ruta de Aprendizaje y Madurez de Desarrollo Web Full Stack",
      alt: `Ruta visual para interpretar ${item.title || "la selección de desarrollo web"}: fundamentos, componentes, servidor y portfolio.`,
      caption:
        "El recorrido ordena fundamentos, componentes, servidor y portfolio como preguntas de lectura; cada programa puede cubrir fases y herramientas diferentes.",
    };
  }

  return {
    src: "/images/infografias/infografia-ecosistema-cursos-latam.svg",
    title: "Ecosistema de Formación: Criterios de Selección Editorial",
    alt: `Mapa visual de los criterios para leer ${item.title || "esta selección editorial"}.`,
    caption:
      "Lee el orden junto con el perfil recomendado, la modalidad, la profundidad del temario y la evidencia institucional disponible.",
  };
}

function getEditorialImage(item, fallback = "/images/editorial/estudiante-cursos-online.webp") {
  return [
    item?.imageUrl,
    item?.entity?.imageUrl,
    item?.entities?.[0]?.imageUrl,
    item?.featured_items?.[0]?.entity?.imageUrl,
    fallback,
  ].find(Boolean);
}

function getEditorialImageAlt(item) {
  return `Imagen editorial de ${item?.title || item?.entity?.title || "formación online"}`;
}

function getEditorialImageCaption(item) {
  return `Referencia visual para el análisis editorial de ${item?.title || "esta propuesta formativa"}.`;
}

function getReviewInfographic(item) {
  return getComparisonInfographic({
    ...item,
    slug: `${item?.slug || ""}-${item?.entity?.title || ""}`,
  });
}

function getComparisonScores(item) {
  const sourceScores = item?.scorecard?.dimensions || item?.dimensions;
  if (!Array.isArray(sourceScores)) {
    return [];
  }

  return sourceScores.filter((row) => {
    const scoreA = Number(row.scoreA);
    const scoreB = Number(row.scoreB);
    return row.criterion && Number.isFinite(scoreA) && Number.isFinite(scoreB) && scoreA >= 0 && scoreA <= 10 && scoreB >= 0 && scoreB <= 10;
  }).map((row) => ({
    criterion: row.criterion,
    scoreA: Number(row.scoreA),
    scoreB: Number(row.scoreB),
    note: row.note,
  }));
}

function buildApiUrl(pathname, params = {}) {
  const normalizedPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  const url = new URL(`${API_BASE}${normalizedPath}`, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  return url.pathname + url.search;
}

async function fetchJson(pathname, params = {}, signal) {
  const response = await fetch(buildApiUrl(pathname, params), { signal });
  const payload = await response.json();

  if (!response.ok || !payload.success) {
    throw new Error(payload.message || "No se pudo obtener la informacion.");
  }

  return payload;
}

function clampPage(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function useApiData(pathname, params) {
  const [state, setState] = useState({ data: null, meta: null, loading: true, error: "" });
  const requestKey = JSON.stringify(params);
  const stableParams = useMemo(() => JSON.parse(requestKey), [requestKey]);

  useEffect(() => {
    const controller = new AbortController();

    setState((current) => ({ ...current, loading: true, error: "" }));

    fetchJson(pathname, stableParams, controller.signal)
      .then((payload) => {
        setState({
          data: payload.data,
          meta: payload.meta || null,
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        setState({
          data: null,
          meta: null,
          loading: false,
          error: error.message,
        });
      });

    return () => controller.abort();
  }, [pathname, stableParams]);

  return state;
}

function useDocumentMeta(title, description) {
  useLayoutEffect(() => {
    const previousTitle = document.title;
    const descriptionNode = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionNode ? descriptionNode.getAttribute("content") : null;
    const canonicalHref = `${window.location.origin}${window.location.pathname}`;
    let canonicalNode = document.querySelector('link[rel="canonical"]');
    const createdCanonicalNode = !canonicalNode;
    const previousCanonical = canonicalNode ? canonicalNode.getAttribute("href") : null;

    if (!canonicalNode) {
      canonicalNode = document.createElement("link");
      canonicalNode.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalNode);
    }

    document.title = title;
    if (descriptionNode) {
      descriptionNode.setAttribute("content", description);
    }
    if (canonicalNode) {
      canonicalNode.setAttribute("href", canonicalHref);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionNode && previousDescription !== null) {
        descriptionNode.setAttribute("content", previousDescription);
      }
      if (canonicalNode) {
        if (createdCanonicalNode) {
          canonicalNode.remove();
        } else if (previousCanonical !== null) {
          canonicalNode.setAttribute("href", previousCanonical);
        }
      }
    };
  }, [description, title]);
}

function updateSearchParams(currentSearchParams, updates = {}, resetPage = true) {
  const nextParams = new URLSearchParams(currentSearchParams);

  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
  });

  if (resetPage) {
    nextParams.set("page", "1");
  }

  return nextParams;
}

function LoadingBlock({ label = "Cargando informacion..." }) {
  return (
    <section className="loading-block">
      <div className="spinner" />
      <p>{label}</p>
    </section>
  );
}

function ErrorBlock({ message }) {
  return (
    <section className="error-block">
      <h2>No pudimos cargar esta seccion</h2>
      <p>{message}</p>
    </section>
  );
}

function EmptyState({ title, description, action }) {
  return (
    <section className="empty-state">
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </section>
  );
}

function Hero({ eyebrow, title, description, aside }) {
  return (
    <section className="hero">
      <div className="hero-copy">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <aside className="hero-aside">{aside}</aside>
    </section>
  );
}

function StatCard({ label, value }) {
  return (
    <article className="stat-card">
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function Pagination({ meta, onPageChange }) {
  if (!meta || meta.total_pages <= 1) {
    return null;
  }

  const pages = [];
  const start = Math.max(1, meta.page - 2);
  const end = Math.min(meta.total_pages, meta.page + 2);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return (
    <nav className="pagination" aria-label="Paginacion">
      <button type="button" onClick={() => onPageChange(meta.page - 1)} disabled={meta.page <= 1}>
        Anterior
      </button>
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          className={page === meta.page ? "is-active" : ""}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onPageChange(meta.page + 1)}
        disabled={meta.page >= meta.total_pages}
      >
        Siguiente
      </button>
    </nav>
  );
}

function EditorialCard({ item, eyebrow, extra }) {
  return (
    <article className="card editorial-card">
      <div className="card-body">
        <EditorialImage
          compact
          src={getEditorialImage(item, "")}
          alt={getEditorialImageAlt(item)}
          caption={getEditorialImageCaption(item)}
        />
        <div className="card-tags">
          <span>{eyebrow}</span>
          {item.country ? <span>{item.country}</span> : null}
        </div>
        <h3>
          <Link to={item.path}>{item.title}</Link>
        </h3>
        <p>{item.description || item.decision_summary || item.summary || ""}</p>
        {item.entity ? (
          <p className="editorial-meta">
            {item.entity.title} {item.rating_overall ? `| Rating editorial ${item.rating_overall}` : ""}
          </p>
        ) : null}
        {extra}
      </div>
      <div className="card-footer">
        <Link className="button button-primary" to={item.path}>
          Ver detalle
        </Link>
      </div>
    </article>
  );
}

function SectionList({ title, items, eyebrow }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="page">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
      </div>
      <section className="grid-layout">
        {items.map((item) => (
          <EditorialCard key={item.slug} item={item} eyebrow={eyebrow} />
        ))}
      </section>
    </section>
  );
}

function ProseSections({ sections }) {
  return (
    <section className="article-layout">
      {(sections || []).map((section) => (
        <article className="panel" key={section.heading}>
          <h2>{section.heading}</h2>
          <div className="prose-copy">
            {(section.body || []).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
}

function FAQBlock({ items }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <article className="panel">
      <h2>Preguntas frecuentes</h2>
      <div className="faq-list">
        {items.map((item) => (
          <div key={item.question}>
            <strong>{item.question}</strong>
            <p>{item.answer}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

export function EntityEditorialSection({ profile, relatedContent }) {
  if (!profile && !relatedContent) {
    return null;
  }

  const hasProfile = profile && (profile.editorial_summary || profile.editorial_rating);

  return (
    <section className="page">
      {hasProfile ? (
        <section className="detail-grid editorial-entity-grid">
          <article className="panel">
            <span className="eyebrow">Editorial</span>
            <h2>Lectura editorial</h2>
            <p>{profile.editorial_summary || profile.positioning}</p>
            <div className="chip-row">
              {profile.editorial_rating ? <span>Rating {profile.editorial_rating}</span> : null}
              {profile.verification?.status ? <span>{profile.verification.status}</span> : null}
            </div>
          </article>
          <article className="panel">
            <h2>Fortalezas y alertas</h2>
            <div className="detail-grid editorial-mini-grid">
              <div>
                <strong>Fortalezas</strong>
                <ul className="rich-list">
                  {(profile.strengths || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>Alertas</strong>
                <ul className="rich-list">
                  {(profile.cautions || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        </section>
      ) : null}

      <div className="detail-grid editorial-entity-grid">
        <RelatedContentPanel title="Resenas relacionadas" items={relatedContent?.reviews} />
        <RelatedContentPanel title="Comparativas relacionadas" items={relatedContent?.comparisons} />
        <RelatedContentPanel title="Listicles relacionados" items={relatedContent?.listicles} />
      </div>
    </section>
  );
}

function RelatedContentPanel({ title, items }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <article className="panel">
      <h2>{title}</h2>
      <div className="link-stack">
        {items.map((item) => (
          <Link key={item.path} to={item.path}>
            {item.title}
          </Link>
        ))}
      </div>
    </article>
  );
}

export function EditorialHubPage() {
  const hubState = useApiData("/editorial", {});
  const hub = hubState.data;

  useDocumentMeta("Guías y Análisis Editorial | Cursos a Distancia", DEFAULT_DESCRIPTION);

  if (hubState.loading && !hub) {
    return <LoadingBlock label="Cargando editorial..." />;
  }

  if (hubState.error) {
    return <ErrorBlock message={hubState.error} />;
  }

  return (
    <div className="page">
      <Hero
        eyebrow="Editorial"
        title="Reseñas, comparativas y listados de cursos analizados"
        description="Análisis comparativos exhaustivos, revisiones institucionales y selecciones formativas conectadas con el directorio oficial de instituciones y programas de formación a distancia."
        aside={
          <div className="stats-grid">
            <StatCard label="Resenas" value={hub?.overview?.reviews || "-"} />
            <StatCard label="Comparativas" value={hub?.overview?.comparisons || "-"} />
            <StatCard label="Listicles" value={hub?.overview?.listicles || "-"} />
          </div>
        }
      />

      <InfographicViewer
        src="/images/infografias/infografia-ecosistema-cursos-latam.svg"
        alt="Mapa visual de las relaciones entre instituciones, programas y modalidades del directorio editorial."
        title="Ecosistema de Educación a Distancia en Latinoamérica"
        caption="Estructura comparada de los 4 grandes modelos formativos: Universidades Oficiales, Bootcamps Tech, Institutos Técnicos y Plataformas Globales de Autoaprendizaje."
      />

      <section className="detail-grid editorial-entity-grid">
        <article className="panel">
          <h2>Metodologia</h2>
          <p>{hub?.methodology?.description}</p>
          <Link className="button button-primary" to="/resenas">
            Ver resenas
          </Link>
        </article>
        <article className="panel">
          <h2>Búsquedas y Especialidades Frecuentes</h2>
          <div className="link-stack">
            {(hub?.query_ownership || []).slice(0, 5).map((item) => (
              <Link key={item.path} to={item.path}>
                {item.query}
              </Link>
            ))}
          </div>
        </article>
      </section>

      <SectionList title="Resenas destacadas" eyebrow="Resenas" items={hub?.featured_reviews || []} />
      <SectionList
        title="Comparativas destacadas"
        eyebrow="Comparativas"
        items={hub?.featured_comparisons || []}
      />
      <SectionList title="Listicles destacados" eyebrow="Mejores" items={hub?.featured_listicles || []} />

      <TrustSignalsGrid />
    </div>
  );
}

function EditorialIndexPage({ endpoint, eyebrow, title, description, emptyTitle, routeBase, infographic }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = clampPage(searchParams.get("page"), 1);
  const entityType = searchParams.get("entity_type") || "";
  const qParam = searchParams.get("q") || "";
  const [searchDraft, setSearchDraft] = useState(qParam);
  const deferredSearch = useDeferredValue(searchDraft);

  useEffect(() => {
    setSearchDraft(qParam);
  }, [qParam]);

  useEffect(() => {
    if (deferredSearch === qParam) {
      return;
    }

    startTransition(() => {
      setSearchParams(updateSearchParams(searchParams, { q: deferredSearch }));
    });
  }, [deferredSearch, qParam, searchParams, setSearchParams]);

  const state = useApiData(endpoint, {
    page,
    page_size: 12,
    entity_type: entityType,
    q: qParam,
  });

  return (
    <div className="page">
      <Hero eyebrow={eyebrow} title={title} description={description} aside={null} />

      {infographic ? (
        <InfographicViewer
          src={infographic.src}
          alt={infographic.alt || infographic.title}
          title={infographic.title}
          caption={infographic.caption}
        />
      ) : null}

      <section className="filters-bar editorial-filters">
        <label className="field">
          <span>Buscar</span>
          <input
            type="search"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Titulo, entidad o query"
          />
        </label>
        <label className="field">
          <span>Tipo de entidad</span>
          <select
            value={entityType}
            onChange={(event) =>
              setSearchParams(updateSearchParams(searchParams, { entity_type: event.target.value }))
            }
          >
            <option value="">Todas</option>
            <option value="institution">Instituciones</option>
            <option value="program">Programas</option>
          </select>
        </label>
      </section>

      {state.loading && !state.data ? <LoadingBlock /> : null}
      {state.error ? <ErrorBlock message={state.error} /> : null}
      {state.data && state.data.length === 0 ? (
        <EmptyState title={emptyTitle} description="No hay piezas para ese filtro por ahora." />
      ) : null}
      {state.data && state.data.length > 0 ? (
        <>
          <section className="grid-layout">
            {state.data.map((item) => (
              <EditorialCard key={item.slug} item={{ ...item, path: item.path || `${routeBase}/${item.slug}` }} eyebrow={eyebrow} />
            ))}
          </section>
          <Pagination
            meta={state.meta}
            onPageChange={(nextPage) =>
              setSearchParams(updateSearchParams(searchParams, { page: String(nextPage) }, false))
            }
          />
        </>
      ) : null}
    </div>
  );
}

export function ReviewsPage() {
  useDocumentMeta("Reseñas y Evaluaciones de Instituciones | Cursos a Distancia", DEFAULT_DESCRIPTION);
  return (
    <EditorialIndexPage
      endpoint="/reviews"
      eyebrow="Reseñas"
      title="Reseñas y Evaluaciones Editoriales"
      description="Análisis independientes sobre validez de títulos, calidad pedagógica, plataforma virtual y experiencia real en instituciones educativas."
      emptyTitle="No hay reseñas para este filtro"
      routeBase="/resenas"
      infographic={{
        src: "/images/infografias/infografia-diplomatura-vs-certificacion-universitaria.svg",
        alt: "Criterios visuales para leer una reseña educativa: respaldo, modalidad, acompañamiento y alcance de la certificación.",
        title: "Criterios para leer una reseña educativa",
        caption: "La evaluación combina respaldo institucional, estructura pedagógica, modalidad, acompañamiento y utilidad profesional; no reemplaza la confirmación en la fuente oficial.",
      }}
    />
  );
}

export function ComparisonsPage() {
  useDocumentMeta("Comparativas de Carreras y Cursos Online | Cursos a Distancia", DEFAULT_DESCRIPTION);
  return (
    <EditorialIndexPage
      endpoint="/comparisons"
      eyebrow="Comparativas"
      title="Comparativas de Carreras y Cursos Online"
      description="Comparaciones directas lado a lado entre instituciones, programas y metodologías formativas con matrices de decisión para elegir tu mejor opción."
      emptyTitle="No hay comparativas para este filtro"
      routeBase="/comparativas"
      infographic={{
        src: "/images/infografias/infografia-ecosistema-cursos-latam.svg",
        alt: "Mapa visual para leer una comparativa de formación online por modalidad, acompañamiento, nivel y certificación.",
        title: "Cómo leer una comparativa de formación online",
        caption: "La matriz conecta modelo pedagógico, flexibilidad, acompañamiento, certificación y salida laboral para que la decisión se base en el objetivo real del estudiante.",
      }}
    />
  );
}

export function ListiclesPage() {
  useDocumentMeta("Mejores Cursos y Diplomaturas Online | Cursos a Distancia", DEFAULT_DESCRIPTION);
  return (
    <EditorialIndexPage
      endpoint="/listicles"
      eyebrow="Mejores"
      title="Rankings y Selecciones de Cursos Destacados"
      description="Selecciones editoriales curadas por especialidad y demanda laboral, analizando temarios, certificaciones oficiales y relación calidad-precio."
      emptyTitle="No hay listados disponibles para este filtro"
      routeBase="/mejores"
      infographic={{
        src: "/images/infografias/infografia-ruta-aprendizaje-desarrollo-web.svg",
        alt: "Ruta visual para interpretar un ranking de cursos por fundamentos, herramientas, proyecto y perfil recomendado.",
        title: "Cómo interpretar un ranking de cursos",
        caption: "El orden editorial debe leerse junto con el perfil recomendado, la modalidad, la profundidad del temario y la evidencia institucional disponible.",
      }}
    />
  );
}

function EditorialDetailPage({ endpoint, slug, titleBuilder, descriptionBuilder, renderBody }) {
  const state = useApiData(endpoint, { slug });
  const item = state.data;

  useDocumentMeta(titleBuilder(item), descriptionBuilder(item));

  if (state.loading && !item) {
    return <LoadingBlock />;
  }

  if (state.error) {
    return <ErrorBlock message={state.error} />;
  }

  if (!item) {
    return <EmptyState title="Contenido no encontrado" description="La pieza solicitada no existe." />;
  }

  return renderBody(item);
}

export function ReviewDetailPage() {
  const { slug } = useParams();

  return (
    <EditorialDetailPage
      endpoint="/reviews"
      slug={slug}
      titleBuilder={(item) => (item ? `${item.title} | Cursos a Distancia` : "Resena | Cursos a Distancia")}
      descriptionBuilder={(item) => item?.description || DEFAULT_DESCRIPTION}
      renderBody={(item) => {
        const infographic = getReviewInfographic(item);

        return (
          <div className="page">
          <Hero
            eyebrow="Resena"
            title={item.title}
            description={item.summary || item.description}
            aside={
              <div className="stats-grid">
                <StatCard label="Rating" value={item.rating_overall || "-"} />
                <StatCard label="Entidad" value={item.entity?.title || "-"} />
              </div>
            }
          />

          <EditorialImage
            src={getEditorialImage(item)}
            alt={getEditorialImageAlt(item)}
            caption={getEditorialImageCaption(item)}
          />

          <InfographicViewer
            src={infographic.src}
            alt={infographic.alt || infographic.title}
            title={infographic.title}
            caption={infographic.caption}
          />

          <section className="detail-grid editorial-entity-grid">
            <article className="panel">
              <h2>Veredicto editorial</h2>
              <p>{item.verdict}</p>
              <div className="chip-row">
                {(item.recommended_for || []).map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
            </article>
            <article className="panel">
              <h2>Senales de confianza</h2>
              <ul className="rich-list">
                {(item.trust_signals || []).map((value) => (
                  <li key={value}>{value}</li>
                ))}
              </ul>
            </article>
          </section>

          <ProseSections sections={item.sections} />
          <FAQBlock items={item.faq} />
          <TrustSignalsGrid />

          <div className="detail-grid editorial-entity-grid">
            <RelatedContentPanel title="Comparativas relacionadas" items={item.related_content?.comparisons} />
            <RelatedContentPanel title="Listicles relacionados" items={item.related_content?.listicles} />
            <RelatedContentPanel title="Programas relacionados" items={item.related_content?.programs} />
          </div>
          </div>
        );
      }}
    />
  );
}

export function ComparisonDetailPage() {
  const { slug } = useParams();

  return (
    <EditorialDetailPage
      endpoint="/comparisons"
      slug={slug}
      titleBuilder={(item) =>
        item ? `${item.title} | Cursos a Distancia` : "Comparativa | Cursos a Distancia"
      }
      descriptionBuilder={(item) => item?.description || DEFAULT_DESCRIPTION}
      renderBody={(item) => {
        const infographic = getComparisonInfographic(item);
        const scores = getComparisonScores(item);
        const entityA = item.entities?.[0];
        const entityB = item.entities?.[1];

        return (
          <div className="page">
            <Hero
              eyebrow="Comparativa"
              title={item.title}
              description={item.decision_summary || item.description}
              aside={
                <div className="stats-grid">
                  {(item.entities || []).map((entry) => (
                    <StatCard key={entry.id} label={entry.type} value={entry.title} />
                  ))}
                </div>
              }
            />

            <EditorialImage
              src={getEditorialImage(item)}
              alt={getEditorialImageAlt(item)}
              caption={getEditorialImageCaption(item)}
            />

            <InfographicViewer
              src={infographic.src}
              alt={infographic.alt || infographic.title}
              title={infographic.title}
              caption={infographic.caption}
            />

            <ComparisonDecisionSelector
              scenarios={item.winner_by_scenario || []}
              entities={item.entities || []}
            />

            {scores && scores.length > 0 && entityA && entityB && (
              <DimensionScorecard entityA={entityA} entityB={entityB} scores={scores} />
            )}

            <article className="panel">
              <h2>Escenarios ganadores</h2>
              <div className="faq-list">
                {(item.winner_by_scenario || []).map((entry) => (
                  <div key={entry.scenario}>
                    <strong>{entry.scenario}</strong>
                    <p>
                      {entry.winner?.title || entry.winner?.name}: {entry.reason}
                    </p>
                  </div>
                ))}
              </div>
            </article>

            {item.table_rows && item.table_rows.length > 0 ? (
              <article className="panel">
                <h2>Matriz comparativa detallada</h2>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <caption className="sr-only">Comparación detallada por dimensiones clave</caption>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #cbd5e1" }}>
                        <th scope="col" style={{ padding: "12px 14px" }}>Criterio</th>
                        {(item.entities || []).map((entity) => (
                          <th key={entity.id} scope="col" style={{ padding: "12px 14px", fontWeight: "bold" }}>
                            {entity.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {item.table_rows.map((row) => (
                        <tr key={row.label} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <th scope="row" style={{ padding: "12px 14px", fontWeight: "600" }}>
                            {row.label}
                          </th>
                          {(item.entities || []).map((entity) => (
                            <td key={entity.id} style={{ padding: "12px 14px" }}>
                              {row.values?.[String(entity.id)] || row.values?.[String(entity.slug)] || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            ) : null}

            <ProseSections sections={item.sections} />
            <FAQBlock items={item.faq} />
            <TrustSignalsGrid />

            <div className="detail-grid editorial-entity-grid">
              <RelatedContentPanel title="Comparativas relacionadas" items={item.related_content?.comparisons} />
              <RelatedContentPanel title="Listicles relacionados" items={item.related_content?.listicles} />
              <RelatedContentPanel title="Resenas relacionadas" items={item.related_content?.reviews} />
              <RelatedContentPanel title="Programas relacionados" items={item.related_content?.programs} />
              <RelatedContentPanel title="Instituciones relacionadas" items={item.related_content?.institutions} />
            </div>
          </div>
        );
      }}
    />
  );
}

export function ListicleDetailPage() {
  const { slug } = useParams();

  return (
    <EditorialDetailPage
      endpoint="/listicles"
      slug={slug}
      titleBuilder={(item) => (item ? `${item.title} | Cursos a Distancia` : "Mejores | Cursos a Distancia")}
      descriptionBuilder={(item) => item?.description || DEFAULT_DESCRIPTION}
      renderBody={(item) => {
        const infographic = getListicleInfographic(item);

        return (
          <div className="page">
            <Hero
              eyebrow="Mejores"
              title={item.title}
              description={item.intro || item.description}
              aside={<div className="stats-grid"><StatCard label="Items" value={item.featured_items?.length || 0} /></div>}
            />

            <EditorialImage
              src={getEditorialImage(item)}
              alt={getEditorialImageAlt(item)}
              caption={getEditorialImageCaption(item)}
            />

            <ListiclePodium items={item.featured_items || []} />

            <InfographicViewer
              src={infographic.src}
              alt={infographic.alt || infographic.title}
              title={infographic.title}
              caption={infographic.caption}
            />

          <section className="grid-layout">
            {(item.featured_items || []).map((entry) => (
              <article className="card editorial-card" key={`${entry.position}-${entry.entity.id}`}>
                <div className="card-body">
                  <EditorialImage
                    compact
                    src={getEditorialImage(entry.entity, "")}
                    alt={`Imagen editorial de ${entry.entity.title}`}
                    caption={`Referencia visual para ${entry.entity.title}.`}
                  />
                  <div className="card-tags">
                    <span>Puesto {entry.position}</span>
                    <span>{entry.entity.type === "institution" ? "Institucion" : "Programa"}</span>
                  </div>
                  <h3>
                    <Link to={entry.entity.path}>{entry.entity.title}</Link>
                  </h3>
                  <p>{entry.why_it_made_the_list}</p>
                  <p className="editorial-meta">{entry.best_for}</p>
                </div>
              </article>
            ))}
          </section>

          <ProseSections sections={item.sections} />
          <FAQBlock items={item.faq} />
          <TrustSignalsGrid />

          <div className="detail-grid editorial-entity-grid">
            <RelatedContentPanel title="Resenas relacionadas" items={item.related_content?.reviews} />
            <RelatedContentPanel title="Comparativas relacionadas" items={item.related_content?.comparisons} />
          </div>
        </div>
      );
    }}
    />
  );
}
